// Hook backing the dev impersonation editor section.
// Owns local state, org-list loading, and persistence to storage.
// Per dev sync note c238ce65, the mobile-viewport simulator is also an
// impersonation axis: the editor tracks a pending preset, and Apply commits
// both impersonation roles and viewport together.

import { useCallback, useEffect, useState } from "react";
import type { AuthContextValue } from "@/contexts/AuthContext";
import { useWorkspaceOrg } from "@/contexts/WorkspaceOrgContext";
import {
  clearDevImpersonation,
  readDevImpersonation,
  writeDevImpersonation,
} from "@/lib/devImpersonationStorage";
import { requireSupabase } from "@/lib/supabase";
import { useDevViewport } from "@/hooks/useDevViewport";
import type { DevViewportPresetId } from "@/components/dev/viewport/devViewportPresets";

export type PendingViewport = DevViewportPresetId | "off";

export type DevImpersonationTrustOverride = "auto" | "1" | "2" | "3";

export type UseDevImpersonationEditorArgs = {
  containerOpen: boolean;
  auth: AuthContextValue;
};

export function useDevImpersonationEditor({ containerOpen, auth }: UseDevImpersonationEditorArgs) {
  const { workspaceOrganizationId, setWorkspaceOrganizationId } = useWorkspaceOrg();
  const { state: viewportState, setPreset: commitViewportPreset, isDesktopBrowser } = useDevViewport();
  const [enabled, setEnabled] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [browseAllOrgs, setBrowseAllOrgs] = useState(false);
  const [trustLevelOverride, setTrustLevelOverride] = useState<DevImpersonationTrustOverride>("auto");
  const [pendingViewport, setPendingViewport] = useState<PendingViewport>("off");
  const [platformOrgs, setPlatformOrgs] = useState<{ id: string; name: string }[]>([]);
  const [memberOrgs, setMemberOrgs] = useState<{ id: string; name: string }[]>([]);
  const [msg, setMsg] = useState<string | null>(null);

  const canUse = auth.status === "ready" && auth.realHasRole("platform_dev", "dev");
  const realPlatformDev = auth.status === "ready" && auth.realHasRole("platform_dev");

  const committedViewport: PendingViewport = viewportState.preset ?? "off";
  const viewportDirty = pendingViewport !== committedViewport;

  const loadLists = useCallback(async () => {
    if (auth.status !== "ready" || !auth.session) return;
    const sb = requireSupabase();
    const mids = auth.memberOrganizationIds ?? [];
    if (mids.length) {
      const { data, error } = await sb.from("organizations").select("id, name").in("id", mids);
      if (!error && data) {
        setMemberOrgs(
          (data as { id: string; name: string }[])
            .map((r) => ({ id: r.id, name: r.name?.trim() || r.id }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      }
    } else {
      setMemberOrgs([]);
    }
    setPlatformOrgs([]);
    // Only real platform_dev may call this RPC; impersonating a platform_* role without it causes 400 spam in the network tab.
    if (realPlatformDev) {
      const { data, error } = await sb.rpc("platform_list_orgs");
      if (!error && Array.isArray(data)) {
        setPlatformOrgs(
          ((data as { id: string; name: string }[]) ?? [])
            .map((r) => ({ id: r.id, name: r.name?.trim() || r.id }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      }
    }
  }, [auth.session, auth.status, auth.memberOrganizationIds, realPlatformDev]);

  useEffect(() => {
    if (!containerOpen || !canUse) return;
    void loadLists();
  }, [containerOpen, canUse, loadLists]);

  useEffect(() => {
    if (!containerOpen) return;
    const s = readDevImpersonation();
    setEnabled(s.enabled);
    setSelected(new Set(s.roles));
    setBrowseAllOrgs(s.workspaceBrowseAllOrgs);
    setTrustLevelOverride(
      s.trustLevelOverride === 1 || s.trustLevelOverride === 2 || s.trustLevelOverride === 3
        ? (String(s.trustLevelOverride) as "1" | "2" | "3")
        : "auto"
    );
    setPendingViewport(viewportState.preset ?? "off");
    // viewportState intentionally excluded from deps — we only want to seed
    // on (re-)open, not stomp the user's in-progress selection when storage
    // changes mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerOpen]);

  const toggleRole = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const persist = useCallback(
    (nextEnabled: boolean, roles: Set<string>, browse: boolean) => {
      setMsg(null);
      if (!nextEnabled || roles.size === 0) {
        clearDevImpersonation();
        setMsg("Impersonation cleared.");
        setEnabled(false);
        setSelected(new Set());
        setBrowseAllOrgs(false);
        setTrustLevelOverride("auto");
        return;
      }
      writeDevImpersonation({
        v: 1,
        enabled: true,
        roles: [...roles],
        workspaceBrowseAllOrgs: browse && realPlatformDev,
        trustLevelOverride: trustLevelOverride === "auto" ? null : (Number(trustLevelOverride) as 1 | 2 | 3),
      });
      setMsg("Saved. UI uses simulated roles; APIs still use your real session.");
    },
    [realPlatformDev, trustLevelOverride]
  );

  /**
   * Single Apply path used by `DevImpersonationSettingsSection`. Commits the
   * roles axis (when enabled) AND the viewport axis (always), so a dev can
   * select roles + orgs + viewport together. Either axis alone is enough to
   * make the Apply button live.
   */
  const applyAll = useCallback(() => {
    if (enabled && selected.size > 0) {
      persist(true, selected, browseAllOrgs);
    } else if (!enabled) {
      // user explicitly toggled impersonation off — make sure storage matches
      // their intent so existing role overlays clear out.
      persist(false, new Set(), false);
    }
    if (viewportDirty) {
      commitViewportPreset(pendingViewport === "off" ? null : pendingViewport);
    }
  }, [
    browseAllOrgs,
    commitViewportPreset,
    enabled,
    pendingViewport,
    persist,
    selected,
    viewportDirty,
  ]);

  const canApply = (enabled && selected.size > 0) || viewportDirty;

  const clearAll = useCallback(() => {
    clearDevImpersonation();
    setEnabled(false);
    setSelected(new Set());
    setBrowseAllOrgs(false);
    setTrustLevelOverride("auto");
    setPendingViewport("off");
    commitViewportPreset(null);
    setMsg("Impersonation cleared.");
  }, [commitViewportPreset]);

  const usePlatformPicker =
    (browseAllOrgs && realPlatformDev) ||
    (auth.hasRole("platform_admin", "platform_dev", "platform_trainer") && platformOrgs.length > 0);
  const orgOptions = usePlatformPicker ? platformOrgs : memberOrgs;

  return {
    canUse,
    realPlatformDev,
    enabled,
    setEnabled,
    selected,
    toggleRole,
    browseAllOrgs,
    setBrowseAllOrgs,
    trustLevelOverride,
    setTrustLevelOverride,
    platformOrgs,
    orgOptions,
    workspaceOrganizationId,
    setWorkspaceOrganizationId,
    msg,
    persist,
    applyAll,
    canApply,
    clearAll,
    pendingViewport,
    setPendingViewport,
    viewportActive: viewportState.active,
    viewportDesktopBrowser: isDesktopBrowser,
  };
}

export type DevImpersonationEditorModel = ReturnType<typeof useDevImpersonationEditor>;
